class PurchaseOptions extends HTMLElement {
  constructor() {
    super();
    this.activeDiscount = 0;
    this.onVariantChanged = this.onVariantChanged.bind(this);
    this.onQtyClick = this.onQtyClick.bind(this);
    this.onQtyChange = this.onQtyChange.bind(this);
  }

  connectedCallback() {
    this.basePrice = parseInt(this.dataset.productPrice || '0');
    this.compareAtPrice = parseInt(this.dataset.compareAtPrice || '0');
    this.hasSubscription = this.dataset.hasSubscription === 'true';

    this.config = this.querySelector('#po-config');
    this.DEFAULT_DISCOUNT = parseInt(this.config?.dataset.defaultDiscount || '0');
    this.bottleSize = parseInt(this.config?.dataset.bottleSize || '0');
    this.servingPerDay = parseInt(this.config?.dataset.servingPerDay || '0');

    this.oneTimeInput = this.querySelector('.puschase-input.one-time');
    this.subInput = this.querySelector('.puschase-input.subscription');
    this.cardSingle = this.querySelector('#card-single');
    this.cardSub = this.querySelector('#card-sub');
    this.subExtras = this.querySelector('#sub-extras');
    this.onetimeExtras = this.querySelector('#onetime-extras');
    this.subFreqLabel = this.querySelector('#sub-freq-label');
    this.savePill = this.querySelector('#save-pill');
    this.sellingPlan = this.querySelector('.po-selling-plan-hidden');
    this.priceEl = this.querySelector('.po-price');
    this.compareEl = this.querySelector('.po-compare');
    this.perServingEl = this.querySelector('.po-per-serving');
    this.stockEl = this.querySelector('.po-stock');
    this.chips = this.querySelectorAll('.po-chip');
    this.qtyComponent = this.querySelector('quantity-selector-component');

    this.moneyFormat = window.Shopify?.money_format || '${{amount}}';

    this.attachListeners();

    if (this.hasSubscription && this.cardSub && this.subInput) {
      this.switchSub();
    } else {
      this.activeDiscount = 0;
      this.renderPriceLive();
    }
  }

  disconnectedCallback() {
    this.qtyComponent?.removeEventListener('click', this.onQtyClick);
    this.qtyComponent?.removeEventListener('change', this.onQtyChange);

    // ── FIX: listen on the correct parent target, not document
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.removeEventListener('theme:variant:update', this.onVariantChanged);
  }

  attachListeners() {
    this.oneTimeInput?.addEventListener('change', () => {
      if (this.oneTimeInput.checked) this.switchSingle();
    });

    this.subInput?.addEventListener('change', () => {
      if (this.subInput.checked) this.switchSub();
    });

    this.cardSingle?.addEventListener('click', (e) => {
      if (e.target.closest('quantity-selector-component')) return;
      this.switchSingle();
    });

    this.cardSub?.addEventListener('click', (e) => {
      if (e.target.closest('quantity-selector-component')) return;
      this.switchSub();
    });

    this.chips.forEach((chip) => {
      chip.addEventListener('click', (e) => {
        e.stopPropagation();
        this.chips.forEach((c) => c.classList.remove('active'));
        chip.classList.add('active');
        this.activeDiscount = parseInt(chip.dataset.discount || '0');
        if (this.sellingPlan) this.sellingPlan.value = chip.dataset.plan || '';
        if (this.subFreqLabel) this.subFreqLabel.textContent = chip.dataset.label || '';
        this.renderPriceLive();
      });
    });

    this.qtyComponent?.addEventListener('click', this.onQtyClick);
    this.qtyComponent?.addEventListener('change', this.onQtyChange);

    // ── FIX: listen on section target using FoxTheme's actual event name
    // 'variant:changed' is wrong — FoxTheme fires 'theme:variant:update'
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener('theme:variant:update', this.onVariantChanged);
  }

  onQtyClick(e) {
    e.stopPropagation();
    setTimeout(() => this.renderPriceLive(), 0);
  }

  onQtyChange(e) {
    e.stopPropagation();
    this.renderPriceLive();
  }

  // ── FIX: read from event.detail.resource to match FoxTheme's VariantUpdateEvent
  onVariantChanged(e) {
    const variant = e?.detail?.resource;
    if (!variant) return;

    this.basePrice = variant.price;
    this.compareAtPrice = variant.compare_at_price || 0;
    this.dataset.productPrice = variant.price;
    this.dataset.compareAtPrice = variant.compare_at_price || 0;

    // Reset servings if variant has its own metafield
    // (falls back to existing this.servings if not present)
    this.updateStockStatus(variant);
    this.renderPriceLive();
  }

  updateStockStatus(variant) {
    if (!this.stockEl) return;

    const checkIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>`;

    const crossIcon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
      <circle cx="12" cy="12" r="10"/>
      <line x1="15" y1="9" x2="9" y2="15"/>
      <line x1="9" y1="9" x2="15" y2="15"/>
    </svg>`;

    if (!variant.available) {
      this.stockEl.className = 'po-stock po-stock--out';
      this.stockEl.innerHTML = crossIcon + ' Out of stock';
      return;
    }

    this.stockEl.className = 'po-stock po-stock--in';

    if (variant.inventory_management === 'shopify') {
      if (variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue') {
        this.stockEl.innerHTML = checkIcon + ' Available on backorder';
      } else if (variant.inventory_quantity <= 5) {
        this.stockEl.innerHTML = checkIcon + ` Only ${variant.inventory_quantity} left in stock`;
      } else {
        this.stockEl.innerHTML = checkIcon + ' In stock and ready to ship';
      }
    } else {
      this.stockEl.innerHTML = checkIcon + ' In stock and ready to ship';
    }
  }

  getQty() {
    const input = this.qtyComponent?.querySelector('.quantity-input');
    return Math.max(1, parseInt(input?.value || '1'));
  }

  fmt(cents) {
    const amount = (cents / 100).toFixed(2);
    const amountNoDecimals = Math.round(cents / 100).toString();
    const amountWithComma = (cents / 100).toFixed(2).replace('.', ',');

    return this.moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', amountNoDecimals)
      .replace('{{amount_with_comma_separator}}', amountWithComma);
  }

  renderPriceLive() {
    const base = this.basePrice;
    const qty = this.getQty();
    const unitPrice = Math.round(base * (1 - this.activeDiscount / 100));
    const total = unitPrice * qty;
    // toggele disabled class to minus
    const minusButton = document.querySelector('.quantity-minus');
    minusButton.classList.toggle('disabled', qty < 2);
    // toggele disabled class to minus end
    if (this.priceEl) {
      this.priceEl.textContent = this.fmt(total);
    }

    if (this.compareEl) {
      const showCompare = this.activeDiscount > 0 || this.compareAtPrice > base;
      const compareBase = this.compareAtPrice > base ? this.compareAtPrice : base;
      if (showCompare) {
        this.compareEl.textContent = this.fmt(compareBase * qty);
        this.compareEl.style.display = 'inline';
      } else {
        this.compareEl.style.display = 'none';
      }
    }

    if (this.perServingEl) {
      if (this.bottleSize > 0 && this.servingPerDay > 0) {
        this.perServingEl.textContent = this.fmt((unitPrice * this.servingPerDay) / this.bottleSize) + ' / Serving';
        this.perServingEl.style.display = 'inline';
      } else {
        this.perServingEl.textContent = '';
        this.perServingEl.style.display = 'none';
      }
    }

    if (this.savePill) {
      this.savePill.textContent = this.activeDiscount > 0
        ? 'Save ' + this.activeDiscount + '%'
        : '';
    }
  }

  switchSingle() {
    this.activeDiscount = 0;
    this.cardSingle?.classList.add('active');
    this.cardSub?.classList.remove('active');
    this.subExtras?.classList.add('d-none');
    this.onetimeExtras?.classList.remove('d-none');
    if (this.oneTimeInput) this.oneTimeInput.checked = true;
    if (this.subInput) this.subInput.checked = false;
    if (this.sellingPlan) this.sellingPlan.value = '';
    this.renderPriceLive();
  }

  switchSub() {
    this.subExtras?.classList.remove('d-none');
    this.onetimeExtras?.classList.add('d-none');

    let activeChip = this.querySelector('.po-chip.active');
    if (!activeChip) {
      activeChip = this.querySelector('.po-chip');
      activeChip?.classList.add('active');
    }

    this.activeDiscount = activeChip
      ? parseInt(activeChip.dataset.discount || '0')
      : this.DEFAULT_DISCOUNT;

    if (this.sellingPlan && activeChip) this.sellingPlan.value = activeChip.dataset.plan || '';
    if (this.subFreqLabel && activeChip) this.subFreqLabel.textContent = activeChip.dataset.label || '';

    this.cardSub?.classList.add('active');
    this.cardSingle?.classList.remove('active');
    if (this.subInput) this.subInput.checked = true;
    if (this.oneTimeInput) this.oneTimeInput.checked = false;

    this.renderPriceLive();
  }
}

customElements.define('purchase-options', PurchaseOptions);